import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import type {
  RentGrowthRankingsResponse,
  RentGrowthSummary,
  SubmarketRentGrowth,
  EgiTimeSeriesResponse,
} from '../types/rentGrowth';

/**
 * Fetch all submarket rent growth rankings.
 * Optionally filter by signal (BUY/HOLD/WAIT).
 */
export function useRentGrowthRankings(signal?: string) {
  return useQuery<RentGrowthRankingsResponse>({
    queryKey: ['rent-growth', 'rankings', signal],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (signal) params.signal = signal;
      const { data } = await api.get<RentGrowthRankingsResponse>(
        '/rent-growth/rankings',
        { params }
      );
      return data;
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetch rent growth detail for a single submarket by name.
 */
export function useSubmarketRentGrowth(submarketName: string) {
  return useQuery<SubmarketRentGrowth>({
    queryKey: ['rent-growth', 'submarket', submarketName],
    queryFn: async () => {
      const encoded = encodeURIComponent(submarketName);
      const { data } = await api.get<SubmarketRentGrowth>(
        `/rent-growth/rankings/${encoded}`
      );
      return data;
    },
    enabled: !!submarketName,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetch rent growth summary statistics.
 */
export function useRentGrowthSummary() {
  return useQuery<RentGrowthSummary>({
    queryKey: ['rent-growth', 'summary'],
    queryFn: async () => {
      const { data } = await api.get<RentGrowthSummary>('/rent-growth/summary');
      return data;
    },
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Fetch historical EGI time-series for a submarket.
 * Returns monthly data points spanning up to 16 years.
 */
export function useSubmarketEgiHistory(submarketName: string) {
  return useQuery<EgiTimeSeriesResponse>({
    queryKey: ['rent-growth', 'history', submarketName],
    queryFn: async () => {
      // API uses lowercase submarket names for history lookup
      const encoded = encodeURIComponent(submarketName.toLowerCase());
      const { data } = await api.get<EgiTimeSeriesResponse>(
        `/rent-growth/history/${encoded}`
      );
      return data;
    },
    enabled: !!submarketName,
    staleTime: 30 * 60 * 1000, // 30min
    refetchOnWindowFocus: false,
  });
}
